import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.js';
import { appointmentsApi, type AppointmentSettings } from '@/lib/api/appointments.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

export function AppointmentsSettingsPage() {
  const storeId = useAuthStore((s) => s.user?.storeId);
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['appointment-settings'],
    queryFn: appointmentsApi.getSettings,
  });

  const [form, setForm] = useState<Partial<AppointmentSettings>>({});

  useEffect(() => {
    if (settingsQuery.data) setForm(settingsQuery.data);
  }, [settingsQuery.data]);

  const saveMut = useMutation({
    mutationFn: (body: Partial<AppointmentSettings>) => appointmentsApi.updateSettings(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointment-settings'] });
      toastSuccess('Settings saved');
    },
    onError: (e) => toastError(parseApiError(e)),
  });

  const embedCode = storeId
    ? `<iframe src="${window.location.origin}/book/${storeId}" width="100%" height="720" frameborder="0" title="Book Appointment"></iframe>`
    : '';

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    saveMut.mutate(form);
  };

  const set = <K extends keyof AppointmentSettings>(key: K, value: AppointmentSettings[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  if (settingsQuery.isLoading) return <p className="text-sm text-slate-500">Loading...</p>;

  return (
    <form onSubmit={onSubmit} className="max-w-2xl space-y-8">
      <div>
        <h2 className="text-lg font-bold">Booking Settings</h2>
        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.aptBookingEnabled ?? true}
            onChange={(e) => set('aptBookingEnabled', e.target.checked)}
          />
          Online booking enabled
        </label>
        <label className="mt-2 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.widgetShowPrices ?? false}
            onChange={(e) => set('widgetShowPrices', e.target.checked)}
          />
          Show prices in widget
        </label>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <NumField label="Min notice (hours)" value={form.aptMinNoticeHours} onChange={(v) => set('aptMinNoticeHours', v)} />
          <NumField label="Max advance (days)" value={form.aptMaxAdvanceDays} onChange={(v) => set('aptMaxAdvanceDays', v)} />
          <NumField label="Quote valid (days)" value={form.quoteValidDays} onChange={(v) => set('quoteValidDays', v)} />
          <NumField label="Reminder (minutes before)" value={form.aptReminderMinutes} onChange={(v) => set('aptReminderMinutes', v)} />
        </div>
      </div>

      <div>
        <h2 className="text-lg font-bold">Cancellation</h2>
        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.aptCancelFollowupEnabled ?? true}
            onChange={(e) => set('aptCancelFollowupEnabled', e.target.checked)}
          />
          Send follow-up question on cancel
        </label>
        <label className="mt-2 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.aptCancelNotifyManagerSms ?? false}
            onChange={(e) => set('aptCancelNotifyManagerSms', e.target.checked)}
          />
          Notify manager via SMS on cancellation
        </label>
      </div>

      <div>
        <h2 className="text-lg font-bold">SMS Templates</h2>
        <TextArea label="Confirmation SMS" value={form.aptConfirmationSms ?? ''} onChange={(v) => set('aptConfirmationSms', v)} />
        <TextArea label="Reminder SMS" value={form.aptReminderSms ?? ''} onChange={(v) => set('aptReminderSms', v)} />
        <TextArea label="Quote SMS" value={form.quoteSmsTemplate ?? ''} onChange={(v) => set('quoteSmsTemplate', v)} />
        <TextArea label="Cancel follow-up SMS" value={form.aptCancelFollowupSms ?? ''} onChange={(v) => set('aptCancelFollowupSms', v)} />
        <p className="mt-1 text-xs text-slate-500">
          Merge fields: {'{name}'} {'{services}'} {'{store_name}'} {'{time}'} {'{date}'} {'{store_phone}'}{' '}
          {'{device}'} {'{booking_link}'}
        </p>
      </div>

      <div>
        <h2 className="text-lg font-bold">Embed Code</h2>
        <textarea readOnly className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 p-3 font-mono text-xs" rows={3} value={embedCode} />
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold"
            onClick={() => navigator.clipboard.writeText(embedCode)}
          >
            Copy iFrame code
          </button>
          {storeId ? (
            <a
              href={`/book/${storeId}`}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white"
            >
              Preview widget ↗
            </a>
          ) : null}
        </div>
      </div>

      <button type="submit" disabled={saveMut.isPending} className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white">
        Save Settings
      </button>
    </form>
  );
}

function NumField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block text-sm">
      <span className="font-medium">{label}</span>
      <input
        type="number"
        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
        value={value ?? ''}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="mt-3 block text-sm">
      <span className="font-medium">{label}</span>
      <textarea
        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
